import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Phone, MessageCircle, Globe, ChevronLeft, 
  Clock, Mail, Navigation, Share2, Image as ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getBusinessBySlug, getCityBySlug, getCategories, trackBusinessAction } from '@/lib/api';
import { toast } from 'sonner';

export default function BusinessPage() {
  const { citySlug, businessSlug } = useParams();
  const navigate = useNavigate();
  
  const [business, setBusiness] = useState(null);
  const [city, setCity] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch business with view tracking
        const bizResponse = await getBusinessBySlug(citySlug, businessSlug, true);
        setBusiness(bizResponse.data);
        
        // Fetch city
        const cityResponse = await getCityBySlug(citySlug);
        setCity(cityResponse.data);
        
        // Fetch categories to get category name
        const catResponse = await getCategories();
        const cat = catResponse.data.find(c => c.id === bizResponse.data.category_id);
        setCategory(cat);
        
      } catch (error) {
        console.error('Failed to fetch data:', error);
        if (error.response?.status === 404) {
          navigate(`/${citySlug}`);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [citySlug, businessSlug, navigate]);

  const handleCall = () => {
    if (business?.phone) {
      trackBusinessAction(business.id, 'call');
      window.location.href = `tel:${business.phone}`;
    }
  };

  const handleWhatsApp = () => {
    if (business?.whatsapp) {
      trackBusinessAction(business.id, 'whatsapp');
      window.open(`https://wa.me/${business.whatsapp}`, '_blank');
    }
  };

  const handleWebsite = () => {
    if (business?.website) {
      trackBusinessAction(business.id, 'website');
      window.open(business.website, '_blank');
    }
  };

  const handleDirections = () => {
    if (business?.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(business.address)}`, '_blank');
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: business?.name,
        text: business?.description || '',
        url: window.location.href
      });
    } catch {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link kopyalandı!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!business) {
    return null;
  }

  const allImages = [
    business.cover_image_url,
    ...(business.gallery || [])
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-background page-enter pb-32 md:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/${citySlug}`)}
              data-testid="back-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="font-heading text-xl font-semibold">
              <span className="text-foreground">Firma</span>
              <span className="text-primary">Burada</span>
            </Link>
          </div>
          <Button variant="ghost" size="icon" onClick={handleShare}>
            <Share2 className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Cover Image / Gallery */}
        {allImages.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative mb-8"
          >
            <div className="aspect-video rounded-xl overflow-hidden bg-card">
              <img 
                src={allImages[activeImage]} 
                alt={business.name}
                className="w-full h-full object-cover"
              />
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${
                      idx === activeImage ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="aspect-video rounded-xl overflow-hidden bg-card flex items-center justify-center mb-8"
          >
            <ImageIcon className="w-16 h-16 text-muted-foreground/30" />
          </motion.div>
        )}

        {/* Business Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-start gap-4 mb-4">
            {business.logo_url && (
              <img 
                src={business.logo_url} 
                alt={business.name}
                className="w-16 h-16 rounded-xl object-cover"
              />
            )}
            <div className="flex-1">
              <h1 className="font-heading text-3xl font-semibold mb-2" data-testid="business-name">
                {business.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2">
                {category && (
                  <Badge variant="secondary">{category.name}</Badge>
                )}
                {city && (
                  <Badge variant="outline" className="border-white/10">
                    <MapPin className="w-3 h-3 mr-1" />
                    {city.name}
                    {business.district && `, ${business.district}`}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {business.description && (
            <p className="text-muted-foreground leading-relaxed">
              {business.description}
            </p>
          )}
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-white/10 rounded-xl p-6 mb-8"
        >
          <h2 className="font-heading text-lg font-semibold mb-4">İletişim Bilgileri</h2>
          
          <div className="space-y-4">
            {business.address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <span className="text-foreground">{business.address}</span>
              </div>
            )}
            
            {business.phone && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-muted-foreground" />
                <a href={`tel:${business.phone}`} className="text-foreground hover:text-primary transition-colors">
                  {business.phone}
                </a>
              </div>
            )}
            
            {business.whatsapp && (
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-green-500" />
                <a 
                  href={`https://wa.me/${business.whatsapp}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-foreground hover:text-green-500 transition-colors"
                >
                  {business.whatsapp}
                </a>
              </div>
            )}
            
            {business.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <a href={`mailto:${business.email}`} className="text-foreground hover:text-primary transition-colors">
                  {business.email}
                </a>
              </div>
            )}
            
            {business.website && (
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-muted-foreground" />
                <a 
                  href={business.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-foreground hover:text-primary transition-colors truncate"
                >
                  {business.website}
                </a>
              </div>
            )}
          </div>
        </motion.div>

        {/* Working Hours */}
        {business.working_hours && Object.keys(business.working_hours).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-white/10 rounded-xl p-6 mb-8"
          >
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Çalışma Saatleri
            </h2>
            
            <div className="space-y-2">
              {Object.entries(business.working_hours).map(([day, hours]) => (
                <div key={day} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{day}</span>
                  <span className="text-foreground">{hours}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Desktop CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {business.phone && (
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90"
              onClick={handleCall}
              data-testid="call-button"
            >
              <Phone className="w-5 h-5 mr-2" /> Ara
            </Button>
          )}
          
          {business.whatsapp && (
            <Button 
              size="lg" 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleWhatsApp}
              data-testid="whatsapp-button"
            >
              <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp
            </Button>
          )}
          
          {business.address && (
            <Button 
              size="lg" 
              variant="secondary"
              onClick={handleDirections}
              data-testid="directions-button"
            >
              <Navigation className="w-5 h-5 mr-2" /> Yol Tarifi
            </Button>
          )}
          
          {business.website && (
            <Button 
              size="lg" 
              variant="secondary"
              onClick={handleWebsite}
              data-testid="website-button"
            >
              <Globe className="w-5 h-5 mr-2" /> Web Sitesi
            </Button>
          )}
        </motion.div>
      </div>

      {/* Mobile Sticky CTAs */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden glass border-t border-white/10 p-4 z-50">
        <div className="grid grid-cols-4 gap-2">
          {business.phone && (
            <Button 
              className="bg-primary hover:bg-primary/90 flex-col h-auto py-3"
              onClick={handleCall}
              data-testid="mobile-call-button"
            >
              <Phone className="w-5 h-5 mb-1" />
              <span className="text-xs">Ara</span>
            </Button>
          )}
          
          {business.whatsapp && (
            <Button 
              className="bg-green-600 hover:bg-green-700 flex-col h-auto py-3"
              onClick={handleWhatsApp}
              data-testid="mobile-whatsapp-button"
            >
              <MessageCircle className="w-5 h-5 mb-1" />
              <span className="text-xs">WhatsApp</span>
            </Button>
          )}
          
          {business.address && (
            <Button 
              variant="secondary"
              className="flex-col h-auto py-3"
              onClick={handleDirections}
              data-testid="mobile-directions-button"
            >
              <Navigation className="w-5 h-5 mb-1" />
              <span className="text-xs">Yol</span>
            </Button>
          )}
          
          {business.website && (
            <Button 
              variant="secondary"
              className="flex-col h-auto py-3"
              onClick={handleWebsite}
              data-testid="mobile-website-button"
            >
              <Globe className="w-5 h-5 mb-1" />
              <span className="text-xs">Web</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
